import React, { useState, useEffect } from 'react';
import { getItemJSON, parseJSONSafe, clearAllSession } from '../utils/storage';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './EmployerDashboard.css';
import showPopup from '../utils/showPopup';

function EmployerDashboard({ user, setUser }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('job-postings');
  const [showJobForm, setShowJobForm] = useState(false);
  const [jobPostings, setJobPostings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [newJob, setNewJob] = useState({
    jobTitle: '',
    jobType: '',
    annualSalaryFrom: '',
    annualSalaryTo: '',
    noOfVacancies: '',
    jobRequirements: '',
    qualification: '',
    experience: '',
    ageLimitFrom: '',
    ageLimitTo: '',
    languagesPreferred: [],
    district: '',
    mandal: '',
    place: '',
    rolesAndResponsibilities: '',
    applicationStartDate: '',
    applicationEndDate: ''
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });

  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Constants for dropdown options
  const jobTypes = [
    'Full Time',
    'Part Time', 
    'Intern',
    'Apprenticeship',
    'Contract',
    'Remote/WFH',
    'Hybrid',
    'On Job Training'
  ];

  const qualifications = [
    '10th fail or below',
    '10th Pass',
    '12th Pass',
    'Graduate (B.A, B.Com, B.Sc, BCA, BE/B.Tech, B.Pharm, BBA, MBBS, Other)',
    'Post Graduate (M.A, M.Com, M.Sc, MD, M.Tech, M.Pharm, MBA, MCA, MSW, Other)',
    'Doctorate/PhD',
    'Diploma',
    'ITI'
  ];

  const experienceLevels = [
    'Fresher',
    '1-2 years',
    '2-3 years',
    '3-5 years',
    '5-8 years',
    '8+ years'
  ];

  const languages = ['Telugu', 'Hindi', 'English'];

  const districts = [
    'Adilabad', 'Bhadradri Kothagudem', 'Hanamkonda', 'Hyderabad', 'Jagtial', 'Jangaon', 'Jayashankar Bhupalpally', 'Jogulamba Gadwal', 'Kamareddy', 'Karimnagar', 'Khammam', 'Kumuram Bheem', 'Mahabubabad', 'Mahabubnagar', 'Mancherial', 'Medak', 'Medchal–Malkajgiri', 'Mulugu', 'Nagarkurnool', 'Nalgonda', 'Narayanpet', 'Nirmal', 'Nizamabad', 'Peddapalli', 'Rajanna Sircilla', 'Rangareddy', 'Sangareddy', 'Siddipet', 'Suryapet', 'Vikarabad', 'Wanaparthy', 'Warangal Rural', 'Yadadri Bhuvanagiri'
  ];

  useEffect(() => {
    // Check if user is logged in
    const employerSession = getItemJSON('employerLoggedIn');
    if (!user && !employerSession) {
      navigate('/login');
      return;
    }
    
    // If user prop is not available but session exists, use session data
    const sessionUser = user || employerSession || null;
    if (!sessionUser) {
      navigate('/login');
      return;
    }

    // Load employer profile from localStorage or backend
    const employerProfile = getItemJSON('employerData');
    if (employerProfile) {
      setProfileData(employerProfile);
    } else if (sessionUser) {
      setProfileData(sessionUser); // fallback to session data
    }

    // Fetch job postings from backend
    const fetchJobPostings = async () => {
      try {
        const response = await fetch(`https://tstranscareers.in/api/api/jobs/employer/${sessionUser.id}`);
        const data = await response.json();
        
        if (data.success) {
          setJobPostings(data.jobs);
          console.log('✅ Loaded jobs from backend:', data.jobs.length);
        } else {
          console.error('Failed to fetch jobs:', data.message);
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };

    // Fetch applications from backend
    const fetchApplications = async () => {
      try {
        const response = await fetch(`https://tstranscareers.in/api/api/applications/employer/${sessionUser.id}`);
        const data = await response.json();
        
        if (data.success) {
          setApplications(data.applications);
          console.log('✅ Loaded applications from backend:', data.applications.length);
        } else {
          console.error('Failed to fetch applications:', data.message);
        }
      } catch (error) {
        console.error('Error fetching applications:', error);
      }
    };

    fetchJobPostings();
    fetchApplications();
  }, [user, navigate]);

  // Track viewport width for mobile nav
  const [isCompact, setIsCompact] = useState(false); // mobile or tablet

  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsCompact(window.innerWidth <= 1024);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Auto-collapse sidebar on compact screens (mobile/tablet)
  useEffect(() => {
    if (isCompact) setSidebarCollapsed(true);
    else setSidebarCollapsed(false);
  }, [isCompact]);

  const handleJobInputChange = (e) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const { checked } = e.target;
      if (checked) {
        setNewJob({
          ...newJob,
          languagesPreferred: [...newJob.languagesPreferred, value]
        });
      } else {
        setNewJob({
          ...newJob,
          languagesPreferred: newJob.languagesPreferred.filter(lang => lang !== value)
        });
      }
    } else {
      setNewJob({
        ...newJob,
        [name]: value
      });
    }
  };

  const validateJobForm = () => {
    const errors = {};
    
    if (!newJob.jobTitle.trim()) errors.jobTitle = 'Job Title is required';
    if (!newJob.jobType) errors.jobType = 'Job Type is required';
    if (!newJob.annualSalaryFrom) errors.annualSalaryFrom = 'Annual Salary From is required';
    if (!newJob.annualSalaryTo) errors.annualSalaryTo = 'Annual Salary To is required';
    if (!newJob.noOfVacancies) errors.noOfVacancies = 'Number of Vacancies is required';
    if (!newJob.jobRequirements.trim()) errors.jobRequirements = 'Job Requirements is required';
    if (!newJob.qualification) errors.qualification = 'Qualification is required';
    if (!newJob.experience) errors.experience = 'Experience is required';
    if (!newJob.district.trim()) errors.district = 'District is required';
    if (!newJob.place.trim()) errors.place = 'Place is required';
    if (!newJob.rolesAndResponsibilities.trim()) errors.rolesAndResponsibilities = 'Roles and Responsibilities is required';
    if (!newJob.applicationStartDate) errors.applicationStartDate = 'Application Start Date is required';
    if (!newJob.applicationEndDate) errors.applicationEndDate = 'Application End Date is required';
    
    return Object.keys(errors).length === 0;
  };

  const handlePostJob = async (e) => {
    e.preventDefault();
    if (validateJobForm()) {
      try {
        const employerSession = getItemJSON('employerLoggedIn');
        const employerId = employerSession?.id;

        if (!employerId) {
          await showPopup('Session expired. Please login again.', 'Session', { type: 'error' });
          return;
        }

        console.log('📝 Posting job to backend...');

        const jobData = {
          employerId,
          ...newJob
        };

        const response = await fetch('https://tstranscareers.in/api/api/jobs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(jobData)
        });

        const data = await response.json();

        if (response.ok && data.success) {
          console.log('✅ Job posted successfully:', data.jobId);
          
          // Refresh job listings
          const jobsResponse = await fetch(`https://tstranscareers.in/api/api/jobs/employer/${employerId}`);
          const jobsData = await jobsResponse.json();
          
          if (jobsData.success) {
            setJobPostings(jobsData.jobs);
          }

          // Reset form
          setNewJob({
            jobTitle: '',
            jobType: '',
            annualSalaryFrom: '',
            annualSalaryTo: '',
            noOfVacancies: '',
            jobRequirements: '',
            qualification: '',
            experience: '',
            ageLimitFrom: '',
            ageLimitTo: '',
            languagesPreferred: [],
            district: '',
            mandal: '',
            place: '',
            rolesAndResponsibilities: '',
            applicationStartDate: '',
            applicationEndDate: ''
          });
          setShowJobForm(false);
          await showPopup('Job posted successfully!', 'Success', { type: 'success' });
        } else {
          await showPopup(data.message || 'Failed to post job. Please try again.', 'Error', { type: 'error' });
        }
      } catch (error) {
        console.error('Error posting job:', error);
        await showPopup('Error posting job. Please try again.', 'Error', { type: 'error' });
      }
    } else {
      await showPopup('Please fill all required fields', 'Validation', { type: 'info' });
    }
  };

  const handleDeleteJob = async (jobId) => {
    const confirm = await showPopup('Are you sure you want to delete this job posting?', 'Confirm', { type: 'info', confirm: true });
    if (!confirm) return;

    try {
      const response = await fetch(`https://tstranscareers.in/api/api/jobs/${jobId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setJobPostings(jobPostings.filter(job => job.id !== jobId));
        await showPopup('Job posting deleted successfully!', 'Success', { type: 'success' });
      } else {
        await showPopup(data.message || 'Failed to delete job. Please try again.', 'Error', { type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting job:', error);
      await showPopup('Error deleting job. Please try again.', 'Error', { type: 'error' });
    }
  };

  const handleApplicationStatusChange = async (applicationId, newStatus, notes = null) => {
    try {
      const response = await fetch(`https://tstranscareers.in/api/api/applications/${applicationId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus, notes: notes })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setApplications(applications.map(app => 
          app.id === applicationId ? { ...app, application_status: newStatus } : app
        ));
        console.log('✅ Application status updated:', newStatus, notes ? `Notes: ${notes}` : '');
        await showPopup(data.message || 'Application status updated', 'Success', { type: 'success' });
      } else {
        await showPopup(data.message || 'Failed to update application status.', 'Error', { type: 'error' });
      }
    } catch (error) {
      console.error('Error updating application status:', error);
      await showPopup('Error updating application status.', 'Error', { type: 'error' });
    }
  };

  // Local UI state for interview modal
  const [interviewModalOpen, setInterviewModalOpen] = useState(false);
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [interviewPlace, setInterviewPlace] = useState('');
  const [interviewLink, setInterviewLink] = useState('');
  const [pendingApplicationId, setPendingApplicationId] = useState(null);

  const handleStatusSelect = async (applicationId, value) => {
    // For interview, open modal to collect date/time/place/link
    if (value === 'interview') {
      setPendingApplicationId(applicationId);
      setInterviewDate('');
      setInterviewTime('');
      setInterviewPlace('');
      setInterviewLink('');
      setInterviewModalOpen(true);
      return;
    }

    // For shortlist/reject show a confirmation popup with OK/Cancel
    if (value === 'shortlisted' || value === 'rejected') {
      const ok = await showPopup(`Are you sure you want to mark this application as '${value}'? Click OK to proceed.`, 'Confirm', { type: 'info', confirm: true });
      if (!ok) return;
      handleApplicationStatusChange(applicationId, value);
      return;
    }

    // For other statuses apply directly
    handleApplicationStatusChange(applicationId, value);
  };

  const submitInterviewSchedule = async () => {
    if (!pendingApplicationId) return;
    if (!interviewDate || !interviewTime) {
      await showPopup('Please enter date and time for the interview.', 'Info', { type: 'info' });
      return;
    }

    // Build schedule details (place and link are optional)
    let scheduled = `Date: ${interviewDate} at ${interviewTime}`;
    if (interviewPlace) {
      scheduled += ` | Place: ${interviewPlace}`;
    }
    if (interviewLink) {
      scheduled += ` | Link: ${interviewLink}`;
    }

    setInterviewModalOpen(false);
    setPendingApplicationId(null);

    await handleApplicationStatusChange(pendingApplicationId, 'interview', scheduled);
  };

  const handleLogout = async () => {
    const ok = await showPopup('Are you sure you want to logout?', 'Confirm', { type: 'info', confirm: true });
    if (!ok) return;
    try {
      clearAllSession();
    } catch (e) {
      console.warn('Error clearing session', e);
    }
    setUser(null);
    navigate('/login', { replace: true });
  };

  // Handle profile input changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save profile changes
  const handleProfileSave = async () => {
    // Optionally send to backend
    // await fetch(`https://tstranscareers.in/api/employer-profile/${profileData.id}`, {
    //   method: 'PUT',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(profileData)
    // });
    localStorage.setItem('employerData', JSON.stringify(profileData));
    setIsEditingProfile(false);
    await showPopup('Profile updated successfully!', 'Success', { type: 'success' });
  };

  // Render profile section
  const renderProfileTab = () => (
    <div className="profile-section">
      <h3>Employer Profile</h3>
      {profileData ? (
        <form className="profile-form">
          <div className="form-row">
            <div className="form-group">
              <label>Company Name</label>
              <input
                type="text"
                name="companyName"
                value={profileData.companyName || ''}
                onChange={handleProfileChange}
                disabled={!isEditingProfile}
              />
            </div>
            <div className="form-group">
              <label>Brand Name</label>
              <input
                type="text"
                name="brandName"
                value={profileData.brandName || ''}
                onChange={handleProfileChange}
                disabled={!isEditingProfile}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Sector</label>
              <input
                type="text"
                name="sector"
                value={profileData.sector || ''}
                onChange={handleProfileChange}
                disabled={!isEditingProfile}
              />
            </div>
            <div className="form-group">
              <label>Website</label>
              <input
                type="text"
                name="website"
                value={profileData.website || ''}
                onChange={handleProfileChange}
                disabled={!isEditingProfile}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Contact Person</label>
              <input
                type="text"
                name="contactPersonName"
                value={profileData.contactPersonName || ''}
                onChange={handleProfileChange}
                disabled={!isEditingProfile}
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="emailId"
                value={profileData.emailId || ''}
                onChange={handleProfileChange}
                disabled={!isEditingProfile}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Mobile Number</label>
              <input
                type="text"
                name="mobileNumber"
                value={profileData.mobileNumber || ''}
                onChange={handleProfileChange}
                disabled={!isEditingProfile}
              />
            </div>
            <div className="form-group">
              <label>District</label>
              <input
                type="text"
                name="district"
                value={profileData.district || ''}
                onChange={handleProfileChange}
                disabled={!isEditingProfile}
              />
            </div>
          </div>
          {/* Add more fields as needed */}
          <div className="edit-actions">
            {!isEditingProfile ? (
              <button type="button" className="btn btn-primary" onClick={() => setIsEditingProfile(true)}>
                {t('common.editProfile')}
              </button>
            ) : (
              <>
                <button type="button" className="btn btn-success" onClick={handleProfileSave}>
                  {t('common.save')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setIsEditingProfile(false)}>
                  {t('common.cancel')}
                </button>
              </>
            )}
          </div>
        </form>
      ) : (
        <p>No profile data found.</p>
      )}
    </div>
  );

  const renderJobPostingsTab = () => (
    <div className="job-postings-section">
      <div className="section-header">
        <h3>{t('employer.myJobPostings')}</h3>
        <button 
          className="post-job-btn"
          onClick={() => setShowJobForm(!showJobForm)}
        >
          {showJobForm ? t('employer.cancel') : t('employer.postNewJob')}
        </button>
      </div>

      {showJobForm && (
        <div className="job-form-section">
          <h4>{t('employer.postNewJob')}</h4>
          <form onSubmit={handlePostJob} className="job-form">
            <div className="form-section">
              <h5>Employer Details</h5>
              <div className="form-group">
                <label>Employer Name</label>
                <input
                  type="text"
                  value={currentUser.name}
                  disabled
                  className="disabled-input"
                />
              </div>
            </div>

            <div className="form-section">
              <h5>Job Details</h5>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="jobTitle">Job Title *</label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    value={newJob.jobTitle}
                    onChange={handleJobInputChange}
                    placeholder="e.g., Software Developer"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="jobType">Job Type *</label>
                  <select
                    id="jobType"
                    name="jobType"
                    value={newJob.jobType}
                    onChange={handleJobInputChange}
                    required
                  >
                    <option value="">Select Job Type</option>
                    {jobTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="annualSalaryFrom">Annual Salary From *</label>
                  <input
                    type="number"
                    id="annualSalaryFrom"
                    name="annualSalaryFrom"
                    value={newJob.annualSalaryFrom}
                    onChange={handleJobInputChange}
                    placeholder="50000"
                    min="50000"
                    max="10000000"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="annualSalaryTo">Annual Salary To *</label>
                  <input
                    type="number"
                    id="annualSalaryTo"
                    name="annualSalaryTo"
                    value={newJob.annualSalaryTo}
                    onChange={handleJobInputChange}
                    placeholder="1000000"
                    min="50000"
                    max="10000000"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="noOfVacancies">No of Vacancies *</label>
                  <input
                    type="number"
                    id="noOfVacancies"
                    name="noOfVacancies"
                    value={newJob.noOfVacancies}
                    onChange={handleJobInputChange}
                    placeholder="1"
                    min="1"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="qualification">Qualification *</label>
                  <select
                    id="qualification"
                    name="qualification"
                    value={newJob.qualification}
                    onChange={handleJobInputChange}
                    required
                  >
                    <option value="">Select Qualification</option>
                    {qualifications.map(qual => (
                      <option key={qual} value={qual}>{qual}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="experience">Experience *</label>
                  <select
                    id="experience"
                    name="experience"
                    value={newJob.experience}
                    onChange={handleJobInputChange}
                    required
                  >
                    <option value="">Select Experience</option>
                    {experienceLevels.map(exp => (
                      <option key={exp} value={exp}>{exp}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Age Limit (Optional)</label>
                  <div className="age-limit-inputs">
                    <input
                      type="number"
                      name="ageLimitFrom"
                      value={newJob.ageLimitFrom}
                      onChange={handleJobInputChange}
                      placeholder="18"
                      min="18"
                      max="65"
                    />
                    <span>{t('common.to')}</span>
                    <input
                      type="number"
                      name="ageLimitTo"
                      value={newJob.ageLimitTo}
                      onChange={handleJobInputChange}
                      placeholder="65"
                      min="18"
                      max="65"
                    />
                    <span>Years</span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Languages Preferred</label>
                <div className="languages-checkboxes">
                  {languages.map(lang => (
                    <label key={lang} className="language-checkbox">
                      <input
                        type="checkbox"
                        name="languagesPreferred"
                        value={lang}
                        checked={newJob.languagesPreferred.includes(lang)}
                        onChange={handleJobInputChange}
                      />
                      {lang}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="jobRequirements">Job Requirements *</label>
                <textarea
                  id="jobRequirements"
                  name="jobRequirements"
                  value={newJob.jobRequirements}
                  onChange={handleJobInputChange}
                  placeholder="e.g., React, JavaScript, 3+ years experience"
                  rows="3"
                  required
                />
              </div>
            </div>

            <div className="form-section">
              <h5>Job Location Details</h5>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="district">District *</label>
                  <select
                    id="district"
                    name="district"
                    value={newJob.district}
                    onChange={handleJobInputChange}
                    required
                  >
                    <option value="">Select District</option>
                    {districts.map(district => (
                      <option key={district} value={district}>{district}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="mandal">Mandal/Taluka/Block</label>
                  <input
                    type="text"
                    id="mandal"
                    name="mandal"
                    value={newJob.mandal}
                    onChange={handleJobInputChange}
                    placeholder="e.g., Secunderabad"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="place">Place *</label>
                <input
                  type="text"
                  id="place"
                  name="place"
                  value={newJob.place}
                  onChange={handleJobInputChange}
                  placeholder="e.g., Hyderabad"
                  required
                />
              </div>
            </div>

            <div className="form-section">
              <h5>Job Description</h5>
              <div className="form-group">
                <label htmlFor="rolesAndResponsibilities">Roles and Responsibilities *</label>
                <textarea
                  id="rolesAndResponsibilities"
                  name="rolesAndResponsibilities"
                  value={newJob.rolesAndResponsibilities}
                  onChange={handleJobInputChange}
                  placeholder="Describe the roles and responsibilities for this position..."
                  rows="4"
                  required
                />
              </div>
            </div>

            <div className="form-section">
              <h5>Job Listing Details</h5>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="applicationStartDate">Application Start Date *</label>
                  <input
                    type="date"
                    id="applicationStartDate"
                    name="applicationStartDate"
                    value={newJob.applicationStartDate}
                    onChange={handleJobInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="applicationEndDate">Application End Date *</label>
                  <input
                    type="date"
                    id="applicationEndDate"
                    name="applicationEndDate"
                    value={newJob.applicationEndDate}
                    onChange={handleJobInputChange}
                    required
                  />
                </div>
              </div>
            </div>

            <button type="submit" className="submit-btn">{t('employer.postJob')}</button>
          </form>
        </div>
      )}

      <div className="job-postings-list">
        {jobPostings.length === 0 ? (
          <p className="no-data">{t('employer.noJobPostings')}</p>
        ) : (
          jobPostings.map(job => (
            <div key={job.id} className="job-posting-card">
              <div className="job-posting-header">
                <h4>{job.job_title || job.jobTitle}</h4>
                <div className="job-actions">
                  <span className="applications-count">{job.applications || 0} {t('employer.applicationsCount')}</span>
                  <button 
                    className="delete-job-btn"
                    onClick={() => handleDeleteJob(job.id)}
                  >
                    {t('employer.delete')}
                  </button>
                </div>
              </div>
              <div className="job-posting-details">
                <div className="detail-row">
                  <p><strong>Job Type:</strong> {job.job_type || job.jobType}</p>
                  <p><strong>Salary:</strong> ₹{job.annual_salary_from || job.annualSalaryFrom} - ₹{job.annual_salary_to || job.annualSalaryTo}</p>
                  <p><strong>Vacancies:</strong> {job.no_of_vacancies || job.noOfVacancies}</p>
                </div>
                <div className="detail-row">
                  <p><strong>Qualification:</strong> {job.qualification}</p>
                  <p><strong>Experience:</strong> {job.experience}</p>
                  <p><strong>Location:</strong> {job.district}, {job.place}</p>
                </div>
                {(job.age_limit_from || job.ageLimitFrom) && (job.age_limit_to || job.ageLimitTo) && (
                  <p><strong>Age Limit:</strong> {job.age_limit_from || job.ageLimitFrom} - {job.age_limit_to || job.ageLimitTo} Years</p>
                )}
                <p><strong>Languages:</strong> {(job.languagesPreferred || []).join(', ')}</p>
                <p><strong>Requirements:</strong> {job.job_requirements || job.jobRequirements}</p>
                <p><strong>Application Period:</strong> {job.application_start_date || job.applicationStartDate} {t('common.to')} {job.application_end_date || job.applicationEndDate}</p>
                <p><strong>Posted:</strong> {new Date(job.created_at || job.postedDate).toLocaleDateString()}</p>
              </div>
              <div className="job-description">
                <h5>Roles and Responsibilities:</h5>
                <p>{job.roles_and_responsibilities || job.rolesAndResponsibilities}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderApplicationsTab = () => (
    <div className="applications-section">
      <h3>{t('employer.applicationsReceived')}</h3>
      {applications.length === 0 ? (
        <p className="no-data">{t('employer.noApplications')}</p>
      ) : (
        <div className="applications-list">
          {applications.map(app => (
            <div key={app.id} className="application-card">
              <div className="application-info">
                <h4>{app.job_title || app.jobTitle}</h4>
                <p className="candidate-name"><strong>Candidate:</strong> {app.candidateName}</p>
                <p className="candidate-email"><strong>Email:</strong> {app.emailId || app.candidateEmail}</p>
                <p className="candidate-phone"><strong>Phone:</strong> {app.mobileNumber || app.candidatePhone}</p>
                <p className="date"><strong>Applied:</strong> {new Date(app.applied_date || app.appliedDate).toLocaleDateString()}</p>
                <p className="qualification"><strong>Qualification:</strong> {app.educationalQualification}</p>
                <p className="location"><strong>Location:</strong> {app.city}, {app.district}</p>
              </div>
              <div className="application-actions">
                <select
                  value={app.application_status || app.status}
                  onChange={(e) => handleStatusSelect(app.id, e.target.value)}
                  className="status-select"
                  aria-label="Change application status"
                >
                  {/* If current status isn't one of the allowed options, show it as a disabled fallback */}
                  {!( ['interview','shortlisted','rejected'].includes(app.application_status || app.status)) && (
                    <option value={app.application_status || app.status} disabled>{app.application_status || app.status}</option>
                  )}
                  <option value="interview">Interview</option>
                  <option value="shortlisted">Shortlisted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Interview scheduling modal (simple inline modal)
  const renderInterviewModal = () => (
    interviewModalOpen ? (
      <div className="modal-backdrop" role="dialog" aria-modal="true">
        <div className="modal-card" style={{ maxWidth: 480, padding: 16 }}>
          <div className="modal-header"><strong>Schedule Interview</strong></div>
          <div className="modal-body">
            <label style={{ display: 'block', marginBottom: 8 }}>Date <span style={{ color: 'red' }}>*</span></label>
            <input type="date" value={interviewDate} onChange={(e) => setInterviewDate(e.target.value)} style={{ width: '100%', padding: 8, marginBottom: 12 }} />
            
            <label style={{ display: 'block', marginBottom: 8 }}>Time <span style={{ color: 'red' }}>*</span></label>
            <input type="time" value={interviewTime} onChange={(e) => setInterviewTime(e.target.value)} style={{ width: '100%', padding: 8, marginBottom: 12 }} />
            
            <label style={{ display: 'block', marginBottom: 8 }}>Place (optional)</label>
            <input type="text" placeholder="e.g., Office address or location" value={interviewPlace} onChange={(e) => setInterviewPlace(e.target.value)} style={{ width: '100%', padding: 8, marginBottom: 12 }} />
            
            <label style={{ display: 'block', marginBottom: 8 }}>Link Address (optional)</label>
            <input type="url" placeholder="e.g., https://meet.google.com/xyz" value={interviewLink} onChange={(e) => setInterviewLink(e.target.value)} style={{ width: '100%', padding: 8 }} />
          </div>
          <div className="modal-footer">
            <button className="btn secondary" onClick={() => { setInterviewModalOpen(false); setPendingApplicationId(null); }}>Cancel</button>
            <button className="btn approve-btn" onClick={submitInterviewSchedule}>OK</button>
          </div>
        </div>
      </div>
    ) : null
  );

  // Check if user is logged in (used for render-level guards)
  const employerSession = getItemJSON('employerLoggedIn');
  const currentUser = user || employerSession || null;

  if (!currentUser) {
    return null;
  }

  return (
    <div className="dashboard">
      <div className="dashboard-layout">
        <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : 'expanded'}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button className="collapse-btn" onClick={() => setSidebarCollapsed(prev => !prev)} aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                <i className="fas fa-bars"></i>
              </button>
              {!sidebarCollapsed && (
                <div>
                  <h3>{t('employer.title', { name: currentUser.name })}</h3>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#6c757d' }}>{currentUser.emailId || ''}</p>
                </div>
              )}
            </div>
          </div>

          <nav className="sidebar-nav">
            <button title={t('employer.profile')} className={`sidebar-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
              <i className="fas fa-id-badge"></i>
              {!sidebarCollapsed && <span>{t('employer.profile')}</span>}
            </button>
            <button title={t('employer.jobPostings')} className={`sidebar-item ${activeTab === 'job-postings' ? 'active' : ''}`} onClick={() => setActiveTab('job-postings')}>
              <i className="fas fa-briefcase"></i>
              {!sidebarCollapsed && <span>{t('employer.jobPostings')}</span>}
            </button>
            <button title={t('employer.applications')} className={`sidebar-item ${activeTab === 'applications' ? 'active' : ''}`} onClick={() => setActiveTab('applications')}>
              <i className="fas fa-file-alt"></i>
              {!sidebarCollapsed && <span>{t('employer.applications')}</span>}
            </button>
          </nav>

          <div className="sidebar-actions">
            <button className="sidebar-action" onClick={() => setShowPasswordModal(true)}>
              <i className="fas fa-key"></i>
              {!sidebarCollapsed && <span>{t('employer.changePassword')}</span>}
            </button>

            <div className="logout-wrap">
              <button className={`sidebar-action danger logout-btn ${sidebarCollapsed ? 'icon-only' : ''}`} onClick={handleLogout}>
                <i className="fas fa-sign-out-alt"></i>
                {!sidebarCollapsed && <span>{t('employer.logout')}</span>}
              </button>
            </div>
          </div>
        </aside>

        <div className="dashboard-main">
          {isCompact && (
            <button
              className="hamburger-btn"
              aria-label={sidebarCollapsed ? 'Open menu' : 'Close menu'}
              onClick={() => setSidebarCollapsed(prev => !prev)}
            >
              <i className="fas fa-bars"></i>
            </button>
          )}

          {isMobile && (
            <div className="mobile-top-nav" role="navigation" aria-label="Mobile Navigation">
              <button className={`sidebar-item ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveTab('profile')}>
                <i className="fas fa-id-badge"></i>
                <span>{t('employer.profile')}</span>
              </button>
              <button className={`sidebar-item ${activeTab === 'job-postings' ? 'active' : ''}`} onClick={() => setActiveTab('job-postings')}>
                <i className="fas fa-briefcase"></i>
                <span>{t('employer.jobPostings')}</span>
              </button>
              <button className={`sidebar-item ${activeTab === 'applications' ? 'active' : ''}`} onClick={() => setActiveTab('applications')}>
                <i className="fas fa-file-alt"></i>
                <span>{t('employer.applications')}</span>
              </button>
            </div>
          )}

          <div className="dashboard-content">
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'job-postings' && renderJobPostingsTab()}
            {activeTab === 'applications' && renderApplicationsTab()}
          </div>
        </div>
      </div>
      {renderInterviewModal()}
      {/* Change Password Modal for Employer */}
      {showPasswordModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Change Password Help">
          <div className="modal-panel">
            <div className="modal-header">
              <h3>{t('common.changePassword')}</h3>
              <button className="close-btn" onClick={() => setShowPasswordModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>If you want to change your password, please contact our helpline.</p>
              <p className="helpline-number">155326</p>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={async () => { try { await navigator.clipboard.writeText('155326'); await showPopup('Copied: 155326', 'Copied', { type: 'success' }); } catch (err) { await showPopup('155326', 'Copied', { type: 'info' }); } }}>Copy</button>
                <button className="btn btn-primary" onClick={() => setShowPasswordModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployerDashboard;